/**
 * FollowUpQuestions Component
 *
 * Displays suggested follow-up questions to continue the conversation.
 * These questions are generated by the backend based on:
 * - The answer just provided
 * - Available topics in the knowledge base
 * - Conversation history
 * - Visitor profile context
 *
 * Features a "surfacing suggestions" animation where questions elegantly
 * slide up and fade in with staggered timing.
 */

import type React from 'react';
import { useEffect, useMemo } from 'react';
import { Analytics } from '../analytics';
import { type ExpandedLayout, getFollowUpQuestionsText } from '../config/constants';
import { areFollowUpSuggestionsDisabled } from '../config/SiteConfigManager';
import { useChatLanguage } from '../context/ChatThemeContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import SvgMaskIcon from './SvgMaskIcon';

interface FollowUpQuestionsProps {
    /** List of suggested follow-up questions */
    questions: string[];
    /** Callback when user clicks a question */
    onQuestionClick: (question: string) => void;
    /** Optional custom class name for styling */
    className?: string;
    /** Active view layout - 'expanded' or 'copilot' */
    viewLayout?: ExpandedLayout;
    /** Whether this is the initial form assistant welcome message (uses different label) */
    isFormAssistantWelcome?: boolean;
}

export const FollowUpQuestions: React.FC<FollowUpQuestionsProps> = ({
    questions,
    onQuestionClick,
    className = '',
    viewLayout = 'expanded',
    isFormAssistantWelcome = false,
}) => {
    const isCopilot = viewLayout === 'copilot';
    // Get the effective language using the theme context
    const effectiveLanguage = useChatLanguage();

    // Get site config to check if follow-up suggestions are disabled
    const { currentDomain } = useSiteConfig();
    const domain = currentDomain || '';

    // Get localized label text - use different label for form assistant welcome message
    const labelText = getFollowUpQuestionsText(
        isFormAssistantWelcome ? 'FORM_ASSISTANT_WELCOME_LABEL' : 'LABEL',
        effectiveLanguage
    );

    // Randomize and limit to maximum 3 questions (memoized to prevent unnecessary re-renders)
    const displayedQuestions = useMemo(() => {
        return [...questions].sort(() => Math.random() - 0.5).slice(0, 3);
    }, [questions]);

    // Track when follow-up questions list is displayed (once per list)
    // Hook must be called unconditionally, so we do checks inside
    useEffect(() => {
        // Skip tracking if disabled or no questions
        if (areFollowUpSuggestionsDisabled(domain) || displayedQuestions.length === 0) {
            return;
        }

        // Create a unique key for this list of questions to prevent duplicate tracking
        const questionsKey = displayedQuestions.join('|');
        const storageKey = `follow_up_questions_displayed_${questionsKey}`;

        // Check if we've already tracked this exact list
        if (sessionStorage.getItem(storageKey)) {
            return;
        }

        // Mark as tracked
        sessionStorage.setItem(storageKey, 'true');

        // Track the display event with all questions
        Analytics.trackFollowUpQuestionsDisplayed({
            questionCount: displayedQuestions.length,
            questions: displayedQuestions,
            language: effectiveLanguage,
        });
    }, [displayedQuestions, effectiveLanguage, domain]);

    // Handle question click with tracking
    const handleQuestionClick = (question: string, index: number) => {
        // Create a unique key for this specific question click
        const storageKey = `follow_up_question_clicked_${question}`;

        // Check if we've already tracked this question click
        if (!sessionStorage.getItem(storageKey)) {
            // Mark as tracked
            sessionStorage.setItem(storageKey, 'true');

            // Track the click event
            Analytics.trackFollowUpQuestionClicked({
                questionText: question,
                questionIndex: index,
                language: effectiveLanguage,
            });
        }

        // Call the original callback
        onQuestionClick(question);
    };

    // Don't render if follow-up suggestions are disabled for this domain
    if (areFollowUpSuggestionsDisabled(domain)) {
        return null;
    }

    // Don't render if no questions
    if (!questions || questions.length === 0) {
        return null;
    }

    return (
        <div className={`rose-follow-up-container mt-6 ${className}`}>
            {/* Label fades in first */}
            <p
                className="rose-follow-up-label text-sm font-medium mb-3"
                style={{
                    animation: 'roseFollowUpLabelFadeIn 0.4s ease-out forwards',
                    opacity: 0,
                    color: 'var(--rw-text-primary)',
                }}
            >
                {labelText}
            </p>
            <div className="rose-follow-up-buttons flex flex-col gap-2.5">
                {displayedQuestions.map((question, index) => (
                    <button
                        key={question}
                        onClick={() => handleQuestionClick(question, index)}
                        className="rose-follow-up-button group flex items-center justify-between py-2 text-left text-base leading-[22px] font-normal cursor-pointer transition-all duration-150 ease-in-out focus:outline-2 focus:outline-offset-2 active:translate-y-px w-fit max-w-full"
                        style={{
                            backgroundImage:
                                'linear-gradient(var(--rw-bg-chip), var(--rw-bg-chip))',
                            border: '1px solid var(--rw-border-chip)',
                            borderRadius: 'var(--rw-radius-pill)',
                            color: 'var(--rw-text-primary)',
                            paddingLeft: isCopilot ? '16px' : '12px',
                            paddingRight: isCopilot ? '16px' : '12px',
                            // Staggered entrance: each question delayed by 100ms
                            animation: 'roseFollowUpSlideUp 0.5s ease-out forwards',
                            animationDelay: `${0.2 + index * 0.1}s`,
                            opacity: 0,
                            transform: 'translateY(12px)',
                        }}
                        type="button"
                        aria-label={`Ask: ${question}`}
                    >
                        <span className="flex-1">{question}</span>
                        <SvgMaskIcon
                            viewBox="0 0 20 20"
                            inner='<path d="M5 10H15M15 10L10 5M15 10L10 15" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
                            width={20}
                            height={20}
                            color="var(--rw-text-secondary)"
                            className="flex-none ml-3 transition-transform group-hover:translate-x-1"
                        />
                    </button>
                ))}
            </div>

            {/* Inline keyframe animations */}
            <style>{`
                @keyframes roseFollowUpLabelFadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(4px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes roseFollowUpSlideUp {
                    from {
                        opacity: 0;
                        transform: translateY(12px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .rose-follow-up-button {
                    background-color: var(--rw-surface-panel);
                }

                .rose-follow-up-button:hover {
                    background-color: var(--rw-surface-interactive-hover);
                }

                .rose-follow-up-button:active {
                    background-color: var(--rw-surface-interactive-active);
                }

                /* Respect user's motion preferences */
                @media (prefers-reduced-motion: reduce) {
                    .rose-follow-up-label,
                    .rose-follow-up-button {
                        animation: none !important;
                        opacity: 1 !important;
                        transform: none !important;
                    }
                }
            `}</style>
        </div>
    );
};
